import React, { useEffect, useRef, useState } from "react";
import { X, Download, Share, Plus, Smartphone, ArrowRight } from "lucide-react";
import { HexLogo } from "@/components/dashboard/Hex";

/**
 * Adaptive PWA install-prompt.
 *
 *   * Detects "already installed" (display-mode: standalone) → renders nothing.
 *   * Fires the native ``beforeinstallprompt`` on Chrome/Edge/Android.
 *   * Falls back to Safari's manual Share → Add to Home Screen flow on iOS
 *     (Apple never fires beforeinstallprompt).
 *   * Remembers dismissal for 7 days so we don't nag.
 *   * Shows only on marketing surfaces (opt-in via the ``showOn`` prop).
 */

const DISMISS_KEY = "lienscout.installPrompt.dismissedUntil";
const DELAY_MS = 4000;
const DISMISS_DAYS = 7;

function detectPlatform() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (isStandalone) return "installed";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS) {
    // iOS PWA install works only from Safari, not Chrome-on-iOS.
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isSafari ? "ios-safari" : "ios-other";
  }
  const isAndroid = /Android/.test(ua);
  const isChromium = /Chrome|Edg|OPR/.test(ua) && !/Firefox/.test(ua);
  if (isAndroid && isChromium) return "android-chromium";
  if (isChromium) return "desktop-chromium";
  return "other";
}

function isSuppressed() {
  try {
    // Allow a manual reset for testing: append ?installprompt=reset to any
    // URL and the 7-day dismissal cooldown is cleared for this device.
    if (typeof window !== "undefined" && window.location?.search?.includes("installprompt=reset")) {
      window.localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function suppress() {
  try {
    window.localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 86400_000)
    );
  } catch (err) {
    console.warn("InstallPrompt suppress() localStorage write failed", err);
  }
}

export function InstallPrompt() {
  const [platform, setPlatform] = useState("unknown");
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === "installed" || p === "unknown") return;
    if (isSuppressed()) return;

    let cancelled = false;
    let dismissed = false;

    // Native prompt handler — Chrome/Edge/Android.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      if (cancelled || dismissed || dismissedRef.current || isSuppressed()) return;
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // For iOS (no native event), show after a short delay.
    // For desktop that DOES support the event, this is a fallback in
    // case the event fires late.
    const timer = window.setTimeout(() => {
      if (cancelled || dismissed || dismissedRef.current || isSuppressed()) return;
      if (
        p === "ios-safari" ||
        p === "ios-other" ||
        p === "android-chromium" ||
        p === "desktop-chromium"
      ) {
        setVisible(true);
      }
    }, DELAY_MS);

    // Hide once app is actually installed.
    const onAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      suppress();
    };
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (!visible || platform === "installed" || platform === "unknown") return null;

  const close = () => {
    dismissedRef.current = true;
    suppress();
    setVisible(false);
    setDeferredPrompt(null);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      } else {
        suppress();
        setVisible(false);
      }
    } catch {
      suppress();
      setVisible(false);
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
    }
  };

  const canFireNative = Boolean(deferredPrompt);
  const isIOS = platform === "ios-safari";
  const isIOSOther = platform === "ios-other"; // Chrome/Firefox on iOS

  return (
    <div
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install Lien Scout"
      className="fixed inset-x-0 bottom-0 md:bottom-6 md:right-6 md:left-auto md:inset-x-auto z-50 md:max-w-sm"
    >
      <div className="mx-3 mb-3 md:mx-0 md:mb-0 bg-brand-black text-white border-2 border-yellow-400 shadow-brutal">
        <div className="flex items-start gap-3 p-4">
          <HexLogo size={44} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-yellow-400">
              Install the app
            </div>
            <div className="font-brand text-lg text-white leading-tight mt-1">
              LIEN SCOUT ON YOUR HOME SCREEN
            </div>
            {isIOS ? (
              <IosInstructions />
            ) : isIOSOther ? (
              <IosOtherBrowserHint />
            ) : (
              <ChromiumBody canFireNative={canFireNative} />
            )}
          </div>
          <button
            data-testid="install-prompt-close"
            onClick={close}
            aria-label="Dismiss"
            className="p-1 text-white/70 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 pb-4">
          {canFireNative ? (
            <button
              data-testid="install-prompt-install-btn"
              onClick={install}
              disabled={installing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black border-2 border-black font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300 disabled:opacity-50"
            >
              <Download size={14} /> {installing ? "Installing…" : "Install now"}
            </button>
          ) : null}
          <button
            data-testid="install-prompt-dismiss"
            onClick={close}
            className="inline-flex items-center gap-2 px-3 py-2 border-2 border-yellow-400 text-yellow-400 font-display font-semibold text-sm hover:bg-yellow-400/10"
          >
            {canFireNative ? "Not now" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChromiumBody({ canFireNative }) {
  if (canFireNative) {
    return (
      <p className="text-sm text-white/80 mt-1 leading-relaxed">
        Get one-tap access. Installs like a real app, no App Store required.
      </p>
    );
  }
  return (
    <div className="text-sm text-white/80 mt-1 space-y-2">
      <p>Click the browser's install icon in the address bar, or:</p>
      <p className="text-xs text-white/70 flex items-center gap-1">
        <span className="font-mono-data font-semibold">⋮</span> menu →
        <span className="font-semibold">Install Lien Scout</span>
      </p>
    </div>
  );
}

function IosInstructions() {
  return (
    <div className="text-sm text-white/80 mt-1 space-y-2">
      <ol className="space-y-2 text-xs">
        <li className="flex items-start gap-2">
          <span className="w-5 h-5 shrink-0 border-2 border-yellow-400 text-yellow-400 text-[10px] font-mono-data flex items-center justify-center">1</span>
          <span className="pt-0.5">
            Tap the <Share size={12} className="inline mx-0.5 -mt-0.5" /> <strong>Share</strong> button at the bottom of Safari
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="w-5 h-5 shrink-0 border-2 border-yellow-400 text-yellow-400 text-[10px] font-mono-data flex items-center justify-center">2</span>
          <span className="pt-0.5">
            Scroll down and choose <Plus size={12} className="inline mx-0.5 -mt-0.5" /> <strong>Add to Home Screen</strong>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="w-5 h-5 shrink-0 border-2 border-yellow-400 text-yellow-400 text-[10px] font-mono-data flex items-center justify-center">3</span>
          <span className="pt-0.5">
            Tap <strong>Add</strong> — the Lien Scout hex will land on your home screen.
          </span>
        </li>
      </ol>
    </div>
  );
}

function IosOtherBrowserHint() {
  return (
    <p className="text-sm text-white/80 mt-1 leading-relaxed">
      To install on iPhone, open this page in <strong>Safari</strong> first
      <Smartphone size={14} className="inline ml-1 -mt-0.5" /> — Apple only supports installing from Safari.
    </p>
  );
}
